import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Modal,
  Fade,
  Backdrop,
  Snackbar,
  Alert,
  Autocomplete,
} from '@mui/material';
import {
  Search,
  Edit,
  Delete,
  FilterAlt,
  Add,
} from '@mui/icons-material';
import CancelIcon from '@mui/icons-material/Cancel';
import {
  getBlockedAirlinesAPI,
  saveBlockedAirlineAPI,
  updateBlockedAirlineAPI,
  deleteBlockedAirlineAPI,
} from '../operations/airlineService';

/* ================= INTERFACES ================= */

interface BlockedAirline {
  id: number | string;
  supplier_name: string;
  supplier_code?: string;
  airline_code: string;
  airline_name: string;
  origin: string;
  destination: string;
  reason: string;
  status: 'active' | 'inactive';
  updated_at: string;
}

interface AirlineOption {
  code: string;
  name: string;
}

interface BlockingFormData {
  supplier: string;
  airline_code: string;
  airline_name: string;
  office_id: string;
  reason: string;
  status: 'active' | 'inactive' | '';
}

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'info' | 'warning';
}

interface Filters {
  search: string;
  supplier: string;
  status: string;
}

/* ================= CONSTANTS ================= */

const SUPPLIER_OPTIONS = ['All Suppliers', 'Mondee', 'Duffel', 'Voyzant', 'Mystifly'];
const SUPPLIER_FORM_OPTIONS = ['Mondee', 'Duffel', 'Voyzant', 'Mystifly'];

const SUPPLIER_CODE_MAP: Record<string, number> = {
  Mondee:   0,
  Voyzant:  0,
  Mystifly: 2,
  Duffel:   3,
};
const STATUS_OPTIONS = ['All', 'Active', 'Inactive'];

const AIRLINE_OPTIONS: AirlineOption[] = [
  { code: 'EK', name: 'Emirates' },
  { code: 'QR', name: 'Qatar Airways' },
  { code: 'SQ', name: 'Singapore Airlines' },
  { code: 'EY', name: 'Etihad Airways' },
  { code: 'AA', name: 'American Airlines' },
  { code: 'DL', name: 'Delta Air Lines' },
  { code: 'UA', name: 'United Airlines' },
  { code: 'BA', name: 'British Airways' },
  { code: 'LH', name: 'Lufthansa' },
  { code: 'AF', name: 'Air France' },
  { code: 'KL', name: 'KLM Royal Dutch Airlines' },
  { code: 'TK', name: 'Turkish Airlines' },
  { code: 'CX', name: 'Cathay Pacific' },
  { code: 'JL', name: 'Japan Airlines' },
  { code: 'NH', name: 'All Nippon Airways' },
  { code: 'MH', name: 'Malaysia Airlines' },
  { code: 'AI', name: 'Air India' },
  { code: 'WY', name: 'Oman Air' },
  { code: 'GF', name: 'Gulf Air' },
  { code: 'FZ', name: 'flydubai' },
  { code: 'G9', name: 'Air Arabia' },
  { code: 'IX', name: 'Air India Express' },
  { code: 'FR', name: 'Ryanair' },
  { code: 'U2', name: 'easyJet' },
  { code: 'W6', name: 'Wizz Air' },
  { code: 'VY', name: 'Vueling Airlines' },
  { code: 'IB', name: 'Iberia' },
  { code: 'AZ', name: 'ITA Airways' },
  { code: 'SK', name: 'SAS' },
  { code: 'AY', name: 'Finnair' },
];

const DEFAULT_FILTERS: Filters = {
  search: '',
  supplier: 'All Suppliers',
  status: 'All',
};

const EMPTY_FORM: BlockingFormData = {
  supplier: '',
  airline_code: '',
  airline_name: '',
  office_id: '',
  reason: '',
  status: '',
};

/* ================= COMPONENT ================= */

const AirlineBlocking = () => {
  const [rows, setRows] = useState<BlockedAirline[]>([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);

  // Create / Edit modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<BlockedAirline | null>(null);
  const [formData, setFormData] = useState<BlockingFormData>(EMPTY_FORM);
  const [submitLoading, setSubmitLoading] = useState(false);

  // Delete dialog
  const [deleteTarget, setDeleteTarget] = useState<BlockedAirline | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Toast
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success',
  });

  useEffect(() => {
    fetchRules();
  }, []);

  const fetchRules = async () => {
    try {
      setLoading(true);
      const response = await getBlockedAirlinesAPI();
      const data = response?.data ?? response ?? [];
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('[AirlineBlocking] fetchRules failed:', err);
    } finally {
      setLoading(false);
    }
  };

  /* Snackbar */
  const showSnackbar = (
    message: string,
    severity: SnackbarState['severity'] = 'success'
  ) => setSnackbar({ open: true, message, severity });

  const handleCloseSnackbar = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') return;
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  /* Filters */
  const handleFilterChange = (key: keyof Filters, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const isFilterActive =
    filters.search.trim() !== '' ||
    filters.supplier !== 'All Suppliers' ||
    filters.status !== 'All';

  const filteredRows = useMemo(() => {
    return rows.filter(row => {
      if (
        filters.supplier !== 'All Suppliers' &&
        row.supplier_name?.toLowerCase() !== filters.supplier.toLowerCase()
      ) return false;

      if (
        filters.status !== 'All' &&
        row.status?.toLowerCase() !== filters.status.toLowerCase()
      ) return false;

      if (filters.search.trim()) {
        const q = filters.search.toLowerCase();
        const matches =
          row.airline_code?.toLowerCase().includes(q) ||
          row.airline_name?.toLowerCase().includes(q) ||
          row.origin?.toLowerCase().includes(q) ||
          row.destination?.toLowerCase().includes(q) ||
          row.reason?.toLowerCase().includes(q);
        if (!matches) return false;
      }

      return true;
    });
  }, [rows, filters]);

  /* Summary stats */
  const totalRules      = rows.length;
  const activeBlocks    = rows.filter(r => r.status === 'active').length;
  const supplierCount   = new Set(rows.map(r => r.supplier_name)).size;
  const airlinesBlocked = new Set(rows.map(r => r.airline_code)).size;

  /* Form helpers */
  const isFormValid =
    formData.supplier.trim() !== '' &&
    formData.airline_code.trim() !== '' &&
    formData.reason.trim().length >= 10 &&
    formData.status !== '';

  const handleFormChange = (field: keyof BlockingFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  /* Open Create modal */
  const handleOpenCreate = () => {
    setEditTarget(null);
    setFormData(EMPTY_FORM);
    setModalOpen(true);
  };

  /* Open Edit modal */
  const handleOpenEdit = (row: BlockedAirline) => {
    setEditTarget(row);
    setFormData({
      supplier:     row.supplier_name ?? '',
      airline_code: row.airline_code  ?? '',
      airline_name: row.airline_name  ?? '',
      office_id:    (row as any).office_id ?? '',
      reason:       row.reason        ?? '',
      status:       row.status        ?? '',
    });
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    if (submitLoading) return;
    setModalOpen(false);
    setEditTarget(null);
    setFormData(EMPTY_FORM);
  };

  /* Submit (Create / Edit) */
  const handleSubmit = async () => {
    if (!isFormValid) return;

    const payload = {
      ...(editTarget ? { id: editTarget.id } : {}),
      supplier_name: formData.supplier,
      supplier_code: SUPPLIER_CODE_MAP[formData.supplier] ?? 0,
      airline_code:  formData.airline_code,
      office_id:     formData.office_id,
      reason:        formData.reason,
      status:        formData.status,
    };

    try {
      setSubmitLoading(true);

      if (editTarget) {
        const updateRes = await updateBlockedAirlineAPI(editTarget.id, payload);
        console.log('[AirlineBlocking] update response:', updateRes);
        if (updateRes?.status === 400 || updateRes?.status >= 400) {
          const detail = Array.isArray(updateRes?.data)
            ? updateRes.data.map((e: any) => e?.message ?? e).join(', ')
            : updateRes?.message ?? 'Update failed';
          throw new Error(detail);
        }
        showSnackbar('Blocking rule updated successfully', 'success');
      } else {
        const saveRes = await saveBlockedAirlineAPI(payload);
        console.log('[AirlineBlocking] save response:', saveRes);
        if (saveRes?.status === 400 || saveRes?.status >= 400) {
          const detail = Array.isArray(saveRes?.data)
            ? saveRes.data.map((e: any) => e?.message ?? e).join(', ')
            : saveRes?.message ?? 'Save failed';
          throw new Error(detail);
        }
        showSnackbar('Blocking rule created successfully', 'success');
      }

      setModalOpen(false);
      setEditTarget(null);
      setFormData(EMPTY_FORM);
      await fetchRules();
    } catch (err: any) {
      const apiMessage = err?.response?.data?.message ?? err?.message ?? 'Unknown error';
      console.error('[AirlineBlocking] submit failed:', err?.response?.status, apiMessage, err);
      showSnackbar(
        editTarget
          ? `Failed to update blocking rule: ${apiMessage}`
          : `Failed to create blocking rule: ${apiMessage}`,
        'error'
      );
    } finally {
      setSubmitLoading(false);
    }
  };

  /* Delete */
  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      setDeleteLoading(true);
      await deleteBlockedAirlineAPI(deleteTarget.id);
      setRows(prev => prev.filter(r => r.id !== deleteTarget.id));
      showSnackbar('Blocking rule deleted successfully', 'success');
      setDeleteTarget(null);
    } catch (err) {
      console.error('[AirlineBlocking] delete failed:', err);
      showSnackbar('Failed to delete blocking rule', 'error');
    } finally {
      setDeleteLoading(false);
    }
  };

/* Selected airline option for Autocomplete */
  const selectedAirline: AirlineOption | null =
    AIRLINE_OPTIONS.find(a => a.code === formData.airline_code) ?? null;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header row */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
            Airline Blocking Configuration
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage supplier-wise airline blocking rules for flight search
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleOpenCreate}
          sx={{
            backgroundColor: '#272727',
            '&:hover': { backgroundColor: '#1a1a1a' },
            textTransform: 'none',
            fontWeight: 600,
            whiteSpace: 'nowrap',
          }}
        >
          Add New
        </Button>
      </Box>

      {/* Summary Cards */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
          gap: 2,
          mb: 3,
        }}
      >
        <Card sx={{ bgcolor: '#ffffff' }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>Total Rules</Typography>
            <Typography variant="h5" fontWeight={700}>{totalRules}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ bgcolor: '#f0fdf4' }}>
          <CardContent>
            <Typography variant="body2" sx={{ fontSize: 12, color: '#15803d' }}>Active Blocks</Typography>
            <Typography variant="h5" fontWeight={700} sx={{ color: '#14532d' }}>{activeBlocks}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ bgcolor: '#eff6ff' }}>
          <CardContent>
            <Typography variant="body2" sx={{ fontSize: 12, color: '#1d4ed8' }}>Suppliers</Typography>
            <Typography variant="h5" fontWeight={700} sx={{ color: '#1e3a8a' }}>{supplierCount}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ bgcolor: '#ffffff' }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>Airlines Blocked</Typography>
            <Typography variant="h5" fontWeight={700}>{airlinesBlocked}</Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Filters */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 3 }}>
        <FilterAlt sx={{ color: '#6b7280' }} />
        <TextField
          placeholder="Search airline code, name, route, reason..."
          size="small"
          sx={{ minWidth: 360, backgroundColor: '#fff' }}
          value={filters.search}
          onChange={e => handleFilterChange('search', e.target.value)}
          InputProps={{ startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} /> }}
        />
        <FormControl size="small" sx={{ minWidth: 160, backgroundColor: '#fff' }}>
          <Select value={filters.supplier} onChange={e => handleFilterChange('supplier', String(e.target.value))}>
            {SUPPLIER_OPTIONS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 130, backgroundColor: '#fff' }}>
          <Select value={filters.status} onChange={e => handleFilterChange('status', String(e.target.value))}>
            {STATUS_OPTIONS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </Select>
        </FormControl>
        {isFilterActive && (
          <Button variant="outlined" size="small" onClick={() => setFilters(DEFAULT_FILTERS)} sx={{ textTransform: 'none' }}>
            Clear Filters
          </Button>
        )}
      </Box>

      {/* Table */}
      <Box sx={{ border: '1px solid #e5e7eb', borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: '70vh', overflowY: 'auto', backgroundColor: '#fff' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow sx={{ boxShadow: 'rgba(0,0,0,0.05) 0px 6px 24px 0px, rgba(0,0,0,0.08) 0px 0px 0px 1px' }}>
                {['SUPPLIER', 'AIRLINE', 'REASON', 'STATUS', 'UPDATED', 'ACTIONS'].map(label => (
                  <TableCell
                    key={label}
                    align="center"
                    sx={{ backgroundColor: '#f1f3f4', fontWeight: 700, whiteSpace: 'nowrap', boxShadow: '0 2px 6px rgba(0,0,0,0.12)' }}
                  >
                    {label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRows.map((row, idx) => (
                <TableRow key={`${row.id}-${idx}`} hover>
                  <TableCell align="center">
                    {row.supplier_name
                      ? row.supplier_name.charAt(0).toUpperCase() + row.supplier_name.slice(1).toLowerCase()
                      : '-'}
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" fontWeight={700} sx={{ lineHeight: 1.4 }}>
                      {row.airline_code || '-'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                      {row.airline_name || ''}
                    </Typography>
                  </TableCell>
                  <TableCell align="center" sx={{ maxWidth: 220 }}>
                    <Tooltip title={row.reason || ''} placement="top" arrow>
                      <Typography
                        variant="body2"
                        sx={{
                          overflow: 'hidden',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical' as const,
                          textOverflow: 'ellipsis',
                          cursor: 'default',
                          textAlign: 'left',
                        }}
                      >
                        {row.reason || '-'}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    {row.status === 'active' ? (
                      <Chip label="Active" size="small" sx={{ color: '#059669', backgroundColor: '#d1fae5', border: '1px solid #a7f3d0', fontWeight: 600 }} />
                    ) : (
                      <Chip label="Inactive" size="small" sx={{ color: '#d97706', backgroundColor: '#fef3c7', border: '1px solid #fde68a', fontWeight: 600 }} />
                    )}
                  </TableCell>
                  <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                    {row.updated_at ? row.updated_at.slice(0, 10) : '-'}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton size="small" color="primary" sx={{ width: 32, height: 32 }} onClick={() => handleOpenEdit(row)}>
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton size="small" sx={{ width: 32, height: 32, color: '#dc2626' }} onClick={() => setDeleteTarget(row)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}

              {!loading && filteredRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ height: 240, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' }}>
                      No Data Found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {loading && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Box sx={{ height: 240, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <CircularProgress />
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Create / Edit Modal */}
      <Modal
        open={modalOpen}
        onClose={handleCloseModal}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 500, sx: { backgroundColor: 'rgba(0,0,0,0.7)' } }}
      >
        <Fade in={modalOpen}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              bgcolor: '#f5f5f5',
              boxShadow: 24,
              p: 4,
              borderRadius: 2,
              maxWidth: 720,
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
            }}
            className="supplierConfigurationPopup"
          >
            <CancelIcon onClick={handleCloseModal} className="closeModal" />

            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
              {editTarget ? 'Edit Blocking Rule' : 'Add New Blocking Rule'}
            </Typography>

            <Box className="feeModalForm">
              <Box className="feeInputFieldWrap" sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>

                {/* Supplier */}
                <Box className="form-control inputFieldWrap" sx={{ flex: '1 1 calc(50% - 16px)', minWidth: 200 }}>
                  <label>Supplier <span style={{ color: '#dc2626' }}>*</span></label>
                  <FormControl sx={{ minWidth: 260 }} className="commondropdownWrap">
                    <Select
                      className="commondropdown superdropdown"
                      value={formData.supplier}
                      onChange={e => handleFormChange('supplier', String(e.target.value))}
                      displayEmpty
                      renderValue={selected =>
                        selected ? selected : <span style={{ color: '#888' }}>Select flight supplier</span>
                      }
                    >
                      <MenuItem value="" disabled>Select flight supplier</MenuItem>
                      {SUPPLIER_FORM_OPTIONS.map(s => (
                        <MenuItem key={s} value={s}>{s}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                {/* Airline */}
                <Box className="form-control inputFieldWrap" sx={{ flex: '1 1 calc(50% - 16px)', minWidth: 200 }}>
                  <label>Airline <span style={{ color: '#dc2626' }}>*</span></label>
                  <Autocomplete
                    options={AIRLINE_OPTIONS}
                    getOptionLabel={option => `${option.code} - ${option.name}`}
                    value={selectedAirline}
                    onChange={(_event, newValue) => {
                      setFormData(prev => ({
                        ...prev,
                        airline_code: newValue?.code ?? '',
                        airline_name: newValue?.name ?? '',
                      }));
                    }}
                    renderInput={params => (
                      <TextField
                        {...params}
                        placeholder="Select airline"
                        size="small"
                        sx={{
                          minWidth: 260,
                          backgroundColor: '#fff',
                          '& .MuiOutlinedInput-root': { borderRadius: '4px' },
                        }}
                      />
                    )}
                    isOptionEqualToValue={(option, value) => option.code === value.code}
                    sx={{ minWidth: 260 }}
                  />
                </Box>

                {/* Office ID */}
                <Box className="form-control inputFieldWrap" sx={{ flex: '1 1 calc(50% - 16px)', minWidth: 200 }}>
                  <label>Office ID</label>
                  <input
                    type="text"
                    className="inputField"
                    placeholder="Enter office ID"
                    style={{ minWidth: '260px', height: '38px' }}
                    value={formData.office_id}
                    onChange={e => handleFormChange('office_id', e.target.value)}
                  />
                </Box>

               

                {/* Status */}
                <Box className="form-control inputFieldWrap" sx={{ flex: '1 1 calc(50% - 16px)', minWidth: 200 }}>
                  <label>Status <span style={{ color: '#dc2626' }}>*</span></label>
                  <FormControl sx={{ minWidth: 260 }} className="commondropdownWrap">
                    <Select
                      className="commondropdown superdropdown"
                      value={formData.status}
                      onChange={e => handleFormChange('status', String(e.target.value))}
                      displayEmpty
                      renderValue={selected =>
                        selected
                          ? selected.charAt(0).toUpperCase() + selected.slice(1)
                          : <span style={{ color: '#888' }}>Select Status</span>
                      }
                    >
                      <MenuItem value="" disabled>Select Status</MenuItem>
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="inactive">Inactive</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                 {/* Reason */}
                <Box className="form-control inputFieldWrap" sx={{ flex: '1 1 calc(50% - 16px)', minWidth: 200 }}>
                  <label>
                    Reason <span style={{ color: '#dc2626' }}>*</span>
                    <span style={{ color: '#9ca3af', fontSize: 12, marginLeft: 6 }}>Min 10 characters</span>
                  </label>
                  <textarea
                    className="inputField"
                    placeholder="Describe why this airline is being blocked"
                    rows={3}
                    style={{
                      minWidth: '260px',
                      height: 'auto',
                      resize: 'vertical',
                      padding: '8px 12px',
                      fontFamily: 'inherit',
                      fontSize: 14,
                      borderRadius: 4,
                      border: formData.reason.trim().length > 0 && formData.reason.trim().length < 10
                        ? '1px solid #dc2626'
                        : '1px solid #d1d5db',
                    }}
                    value={formData.reason}
                    onChange={e => handleFormChange('reason', e.target.value)}
                  />
                  {formData.reason.trim().length > 0 && formData.reason.trim().length < 10 && (
                    <Typography variant="caption" sx={{ color: '#dc2626', mt: 0.5, display: 'block' }}>
                      Reason must be at least 10 characters
                    </Typography>
                  )}
                </Box>

              </Box>

              {/* Submit button */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
                <Button
                  variant="outlined"
                  onClick={handleCloseModal}
                  disabled={submitLoading}
                  sx={{ textTransform: 'none' }}
                >
                  Cancel
                </Button>
                <Tooltip
                  title={
                    isFormValid ? '' : [
                      !formData.supplier && 'Supplier is required',
                      !formData.airline_code && 'Airline is required',
                      formData.reason.trim().length < 10 && 'Reason must be at least 10 characters',
                      !formData.status && 'Status is required',
                    ].filter(Boolean).join(' | ')
                  }
                  arrow
                  placement="top"
                  disableHoverListener={isFormValid}
                >
                  <span>
                    <Button
                      variant="contained"
                      onClick={handleSubmit}
                      disabled={!isFormValid || submitLoading}
                      sx={{
                        backgroundColor: isFormValid ? '#272727' : '#9ca3af',
                        '&:hover': { backgroundColor: isFormValid ? '#1a1a1a' : '#9ca3af' },
                        '&.Mui-disabled': { backgroundColor: '#9ca3af', color: '#fff' },
                        textTransform: 'none',
                        fontWeight: 600,
                      }}
                    >
                      {submitLoading
                        ? <CircularProgress size={22} color="inherit" />
                        : editTarget ? 'Update Rule' : 'Add Rule'}
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            </Box>
          </Box>
        </Fade>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => !deleteLoading && setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete the blocking rule for{' '}
            <strong>{deleteTarget?.airline_code}</strong> under{' '}
            <strong>{deleteTarget?.supplier_name}</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleteLoading} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteLoading}
            sx={{ textTransform: 'none' }}
          >
            {deleteLoading ? <CircularProgress size={18} color="inherit" /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{
          top: '50% !important',
          left: '50% !important',
          transform: 'translate(-50%, -50%) !important',
        }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%', fontSize: '1rem' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AirlineBlocking;
